import * as Sentry from "@sentry/react-native";
import * as Updates from "expo-updates";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { AppState, AppStateStatus, Platform } from "react-native";

import { navigationRef_atom } from "@/features/Navigation";
import { useOtaUpdateOverlayContext } from "@/features/OtaUpdateOverlay";

import { registerBackgroundExpoUpdateTaskAsync } from "./backgroundExpoUpdateTask";
import { prefetchExpoUpdateAsync } from "./prefetchExpoUpdateAsync";

const BACKGROUND_AUTO_APPLY_DELAY_MS = 6 * 60 * 1000;
const HOME_ROUTE_NAME = "MainHome";
const APPLY_UPDATE_OVERLAY_DELAY_MS = 1000;

/**
 *
 * This component handles checking for app visibility.
 * If the app is visible, then we prefetch OTA javascript bundle updates.
 *
 * Updates are applied at cold start (expo-updates launches the newest
 * downloaded bundle), when the app returns from a long background stint, or
 * when a staged update is waiting and the user lands on the home screen.
 *
 */
export const ExpoUpdatesManager = () => {
  if (Platform.OS === "web") {
    return null;
  }

  return <ExpoUpdatesManagerNative />;
};

const ExpoUpdatesManagerNative = () => {
  const isPrefetchingUpdateRef = useRef(false);
  const isApplyingUpdateRef = useRef(false);
  const lastBackgroundedAtRef = useRef<number | null>(null);
  const navigationRef = useAtomValue(navigationRef_atom);
  const { showApplyingUpdateOverlay } = useOtaUpdateOverlayContext();

  // Used only to trigger a re-check when the update becomes pending (see the
  // effect below). Pending state is always read synchronously from
  // `Updates.latestContext` at the point of use to avoid React render lag.
  const { isUpdatePending } = Updates.useUpdates();

  const applyPendingUpdate = useCallback(() => {
    if (isApplyingUpdateRef.current) {
      return;
    }

    isApplyingUpdateRef.current = true;
    showApplyingUpdateOverlay();

    setTimeout(() => {
      Updates.reloadAsync().catch((error) => {
        isApplyingUpdateRef.current = false;
        Sentry.captureException(error);
      });
    }, APPLY_UPDATE_OVERLAY_DELAY_MS);
  }, [showApplyingUpdateOverlay]);

  const applyPendingUpdateIfHome = useCallback(() => {
    if (
      Platform.OS !== "web" &&
      Updates.latestContext.isUpdatePending &&
      navigationRef?.isReady() &&
      navigationRef.getCurrentRoute()?.name === HOME_ROUTE_NAME
    ) {
      applyPendingUpdate();
    }
  }, [applyPendingUpdate, navigationRef]);

  const prefetchUpdates = useCallback(async () => {
    if (isPrefetchingUpdateRef.current) {
      return;
    }

    try {
      isPrefetchingUpdateRef.current = true;
      await prefetchExpoUpdateAsync();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      isPrefetchingUpdateRef.current = false;
    }
  }, []);

  const handleAppStateChange = useCallback(
    (nextAppState: AppStateStatus) => {
      if (nextAppState !== "active") {
        lastBackgroundedAtRef.current = Date.now();
        return;
      }

      // Read pending state synchronously from the native state machine
      // context. The `useUpdates()` value is only updated during a React
      // render, which can lag behind the native event when the JS thread was
      // suspended in the background — so it may still be stale when the
      // "active" event fires. `Updates.latestContext` is updated synchronously
      // when the native updates-state-change event fires.
      if (Updates.latestContext.isUpdatePending) {
        const lastBackgroundedAt = lastBackgroundedAtRef.current;
        if (
          lastBackgroundedAt !== null &&
          Date.now() - lastBackgroundedAt >= BACKGROUND_AUTO_APPLY_DELAY_MS
        ) {
          applyPendingUpdate();
        }
        return;
      }

      prefetchUpdates();
    },
    [applyPendingUpdate, prefetchUpdates]
  );

  useEffect(() => {
    if (Platform.OS !== "web" && isUpdatePending) {
      applyPendingUpdateIfHome();
    }
  }, [applyPendingUpdateIfHome, isUpdatePending]);

  useEffect(() => {
    if (!navigationRef?.isReady()) {
      return;
    }

    applyPendingUpdateIfHome();
    const unsubscribe = navigationRef.addListener(
      "state",
      applyPendingUpdateIfHome
    );

    return unsubscribe;
  }, [applyPendingUpdateIfHome, navigationRef]);

  useEffect(() => {
    registerBackgroundExpoUpdateTaskAsync();
    prefetchUpdates();

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, [handleAppStateChange, prefetchUpdates]);

  return null;
};
